import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Trash2 } from "lucide-react";
import Tooltip from "@mui/material/Tooltip";
import HeaderForm from "../../components/Header";
import ActionButton from "../../components/ActionButton";
import Loader from "../../components/Loader";
import { errorMessage, successMessage } from "../../components/Toaster";
import { formatDateToISOString } from "../../utils/utils";
import { getErrorMessage } from "../../utils/error";
import { setFilters } from "../../store/slices/filterSlice";
import {
  fetchReportData,
  fetchScans,
  fetchScanByContainer,
  removeScan,
  setShippingLineOptions,
} from "../../store/slices/reportSlice";
import Pagination from "../../components/Pagination";
import { SUPER_ADMIN, VISO_SUPER_ADMIN } from "../../constants";
import { deleteScan } from "./api";

const getTimeInYard = (entryTime) => {
  const now = new Date();
  const entry = new Date(entryTime.replace("T", " ").replace("Z", ""));
  const diffMs = now - entry;
  if (diffMs < 0) return { days: 0, hours: 0, minutes: 0 };
  const totalMinutes = Math.floor(diffMs / (1000 * 60));
  const totalHours = Math.floor(diffMs / (1000 * 60 * 60));
  const totalDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  return {
    days: totalDays,
    hours: totalHours % 24,
    minutes: totalMinutes % 60,
  };
};

const LiveTimeInYard = ({ entryTime, isInYard }) => {
  const [time, setTime] = useState(() => getTimeInYard(entryTime));

  useEffect(() => {
    const interval = setInterval(
      () => setTime(getTimeInYard(entryTime)),
      60000,
    );
    return () => clearInterval(interval);
  }, [entryTime]);

  const parts = [];
  if (time.days > 0) parts.push(`${time.days}d`);
  if (time.hours > 0) parts.push(`${time.hours}h`);
  if (time.minutes > 0) parts.push(`${time.minutes}m`);
  const timeLabel = parts.join(" ") || "0m";

  return (
    <Tooltip
      arrow
      title={
        <div style={{ fontSize: 12, lineHeight: 1.8 }}>
          <div>
            {isInYard ? "At Yard" : "Exited"} • {timeLabel}
          </div>
        </div>
      }
    >
      <span
        className={`inline-flex items-center justify-center px-3 py-1 rounded-full font-semibold cursor-pointer ${
          isInYard
            ? "border-2 border-red-500 text-red-700 bg-red-50"
            : "border-2 border-green-500 text-green-700 bg-green-50"
        }`}
      >
        {time.days}
      </span>
    </Tooltip>
  );
};

const YardReport = () => {
  const dispatch = useDispatch();
  const roleName = localStorage.getItem("role_name");
  const filters = useSelector((state) => state.filters);

  const isVisoAdmin = roleName === VISO_SUPER_ADMIN;
  const localCompanyId = localStorage.getItem("company_id");
  const companyId = isVisoAdmin
    ? filters.selectedCompanyId || localCompanyId
    : localCompanyId;

  const {
    scansData,
    yardOptions,
    shippingLineOptions,
    allShippingLines,
    isLoading,
    lastFetchedFilters,
  } = useSelector((state) => state.report);

  const scrollContainerRef = useRef(null);
  const scrollPositionRef = useRef(filters.scrollPosition || 0);
  const hasRestoredScroll = useRef(false);

  useEffect(() => {
    if (isLoading) {
      hasRestoredScroll.current = false;
      return;
    }
    if (hasRestoredScroll.current) return;
    const saved = scrollPositionRef.current;
    if (!saved) return;
    hasRestoredScroll.current = true;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTop = saved;
        }
      });
    });
  }, [isLoading]);

  const saveScrollPosition = () => {
    const top = scrollContainerRef.current?.scrollTop ?? 0;
    scrollPositionRef.current = top;
    dispatch(setFilters({ scrollPosition: top }));
  };

  const currentPage = filters.currentPage || 1;
  const itemsPerPage = filters.itemsPerPage || 20;
  const sortConfig = filters.sortConfig || { key: null, direction: "asc" };
  const activeTab = filters.activeTab || "all";
  const searchQuery = filters.searchQuery || "";
  const [deletingId, setDeletingId] = useState(null);

  const ASSET_LABELS = {
    Container: {
      idLabel: "Container ID",
      capacityLabel: "Container Capacity",
      dropDownLabel: "Containers at Yard For",
      searchPlaceholder: "Search by Container ID…",
      csvIdHeader: "Container ID",
      csvCapacityHeader: "Container Capacity",
    },
    IsoTank: {
      idLabel: "Tank ID",
      capacityLabel: "Tank Capacity",
      dropDownLabel: "Tanks at Yard For",
      searchPlaceholder: "Search by Tank ID…",
      csvIdHeader: "Tank ID",
      csvCapacityHeader: "Tank Capacity",
    },
    Other: {
      idLabel: "Asset ID",
      capacityLabel: "Asset Capacity",
      dropDownLabel: "Assets at Yard For",
      searchPlaceholder: "Search by Asset ID…",
      csvIdHeader: "Asset ID",
      csvCapacityHeader: "Asset Capacity",
    },
  };
  const assetType = localStorage.getItem("asset_type") || "Container";
  const labels = ASSET_LABELS[assetType] || ASSET_LABELS["Container"];

  const listTableHeaders = [
    { key: "sno", label: "S No", sortable: false },
    { key: "container_number", label: labels.idLabel, sortable: false },
    { key: "container_capacity", label: labels.capacityLabel, sortable: false },
    { key: "entry_time", label: "Entry Date", sortable: true },
    { key: "days_in_yard", label: "Days in Yard", sortable: true },
  ];

  useEffect(() => {
    if (!companyId) return;
    if (yardOptions.length > 0) return;

    dispatch(fetchReportData({ companyId, filters }))
      .unwrap()
      .then(({ allYardIds, allShippingLineIds }) => {
        dispatch(
          setFilters({
            activeTab:
              filters.activeTab ||
              (filters.filterMode === "days" ? "at_yard" : "all"),
            yards: filters.yards?.length > 0 ? filters.yards : allYardIds,
            shippingLines: filters.useShippingLineFilter
              ? filters.shippingLines
              : allShippingLineIds,
            useShippingLineFilter: filters.useShippingLineFilter || false,
            filterMode: filters.filterMode || "date",
            days: filters.days,
            currentPage: filters.currentPage || 1,
            itemsPerPage: filters.itemsPerPage || 20,
            sortConfig: filters.sortConfig || { key: null, direction: "asc" },
            searchQuery: filters.searchQuery || "",
            scrollPosition: filters.scrollPosition || 0,
          }),
        );
      })
      .catch((err) => {
        errorMessage(getErrorMessage(err) || "Error fetching data.");
      });
  }, [companyId]);

  useEffect(() => {
    if (yardOptions.length === 0) return;
    if (!filters.yards?.length) return;
    if (filters.filterMode === "container") return; // handled directly by handleSubmit

    const currentFilters =
      filters.filterMode === "days"
        ? {
            yards: filters.yards,
            filterMode: "days",
            days: filters.days,
            shippingLines: filters.useShippingLineFilter
              ? filters.shippingLines
              : [],
            useShippingLineFilter: filters.useShippingLineFilter || false,
          }
        : {
            yards: filters.yards,
            filterMode: "date",
            startDate: formatDateToISOString(filters.startDate, true),
            endDate: formatDateToISOString(filters.endDate, false),
            shippingLines: filters.useShippingLineFilter
              ? filters.shippingLines
              : [],
            useShippingLineFilter: filters.useShippingLineFilter || false,
          };

    const filtersChanged =
      JSON.stringify(lastFetchedFilters) !== JSON.stringify(currentFilters);
    if (!filtersChanged) return;

    const payload = { yard_ids: filters.yards };
    if (filters.useShippingLineFilter && filters.shippingLines?.length > 0)
      payload.shipping_line_ids = filters.shippingLines;

    if (filters.filterMode === "days" && filters.days) {
      payload.days = Number(filters.days);
    } else {
      payload.start_date = formatDateToISOString(filters.startDate, true);
      payload.end_date = formatDateToISOString(filters.endDate, false);
    }

    dispatch(fetchScans(payload))
      .unwrap()
      .catch((err) => {
        errorMessage(getErrorMessage(err) || "Error fetching scans.");
      });
  }, [
    filters.yards,
    filters.startDate,
    filters.endDate,
    filters.days,
    filters.filterMode,
    yardOptions,
  ]);

  useEffect(() => {
    if (!filters.yards?.length) {
      dispatch(setShippingLineOptions([]));
      return;
    }
    const filtered = allShippingLines
      .filter((sl) => filters.yards.includes(sl.yard_id))
      .map((sl) => ({
        label: sl.shipping_line_name,
        value: sl.shipping_line_id,
        yard_id: sl.yard_id,
      }));
    dispatch(setShippingLineOptions(filtered));
  }, [filters.yards, allShippingLines]);

  useEffect(() => {
    if (searchQuery) {
      dispatch(setFilters({ ...filters, currentPage: 1 }));
    }
  }, [searchQuery]);

  const handleTabChange = (tab) => {
    dispatch(setFilters({ ...filters, activeTab: tab, currentPage: 1 }));
  };

  const handleSort = (key) => {
    if (!key) return;
    const direction =
      sortConfig.key === key && sortConfig.direction === "asc" ? "desc" : "asc";
    dispatch(setFilters({ ...filters, sortConfig: { key, direction } }));
  };

  const handleSubmit = async (data) => {
    // ── Container search: bypasses date filters, but respects yard/shipping-line scope ──
    if (data.containerNumber) {
      scrollPositionRef.current = 0;
      dispatch(
        fetchScanByContainer({
          container_number: data.containerNumber,
          yard_ids: data.yards || [],
          shipping_line_ids: data.shippingLines || [],
        }),
      )
        .unwrap()
        .catch((err) => {
          errorMessage(getErrorMessage(err) || "Error fetching container.");
        });
      return;
    }

    const yard_ids = data.yards;
    const shippingLineIds = data.shippingLines || [];
    const payload = { yard_ids };
    if (data.shippingLines?.length > 0)
      payload.shipping_line_ids = shippingLineIds;

    if (data.days) {
      payload.days = Number(data.days);
      dispatch(
        setFilters({
          ...filters,
          yards: yard_ids,
          shippingLines: shippingLineIds,
          days: data.days,
          filterMode: "days",
          activeTab: "at_yard",
          currentPage: 1,
          sortConfig: { key: null, direction: "asc" },
          useShippingLineFilter:
            !!data.shippingLines && data.shippingLines.length > 0,
          scrollPosition: 0,
        }),
      );
    } else {
      payload.start_date = formatDateToISOString(data.startDate, true);
      payload.end_date = formatDateToISOString(data.endDate, false);
      dispatch(
        setFilters({
          ...filters,
          yards: yard_ids,
          shippingLines: shippingLineIds,
          startDate: data.startDate,
          endDate: data.endDate,
          filterMode: "date",
          activeTab: "all",
          currentPage: 1,
          sortConfig: { key: null, direction: "asc" },
          useShippingLineFilter:
            !!data.shippingLines && data.shippingLines.length > 0,
          scrollPosition: 0,
        }),
      );
    }
    scrollPositionRef.current = 0;

    dispatch(fetchScans(payload))
      .unwrap()
      .catch((err) => {
        errorMessage(getErrorMessage(err) || "Error fetching scans.");
      });
  };

  const handleSearchChange = (e) => {
    dispatch(setFilters({ ...filters, searchQuery: e.target.value }));
  };

  const handlePageChange = (page) => {
    scrollPositionRef.current = 0;
    dispatch(setFilters({ ...filters, currentPage: page, scrollPosition: 0 }));
  };

  const handleItemsPerPageChange = (count) => {
    scrollPositionRef.current = 0;
    dispatch(
      setFilters({
        ...filters,
        itemsPerPage: count,
        currentPage: 1,
        scrollPosition: 0,
      }),
    );
  };

  const getFilteredData = (data) => {
    let filtered = data;
    if (activeTab === "at_yard")
      filtered = data.filter((i) => i.is_in_yard === true);
    else if (activeTab === "out_of_yard")
      filtered = data.filter((i) => i.is_in_yard === false);
    if (searchQuery.trim())
      filtered = filtered.filter((i) =>
        i.container_number
          ?.toLowerCase()
          .includes(searchQuery.toLowerCase().trim()),
      );
    return filtered;
  };

  const getSortedData = (data) => {
    if (!sortConfig.key || data.length === 0) return data;
    return [...data].sort((a, b) => {
      let aValue, bValue;
      if (sortConfig.key === "entry_time") {
        aValue = a.entry_time ? new Date(a.entry_time).getTime() : -Infinity;
        bValue = b.entry_time ? new Date(b.entry_time).getTime() : -Infinity;
      } else if (sortConfig.key === "days_in_yard") {
        aValue = a.entry_time ? getTimeInYard(a.entry_time).days : -Infinity;
        bValue = b.entry_time ? getTimeInYard(b.entry_time).days : -Infinity;
      }
      if (aValue === -Infinity && bValue === -Infinity) return 0;
      if (aValue === -Infinity) return 1;
      if (bValue === -Infinity) return -1;
      return sortConfig.direction === "asc" ? aValue - bValue : bValue - aValue;
    });
  };

  const SortIcon = ({ columnKey }) => {
    const isActive = sortConfig.key === columnKey;
    const isAscending = sortConfig.direction === "asc";
    return (
      <span className="inline-flex ml-1.5 align-middle">
        {!isActive ? (
          <svg
            className="w-3.5 h-4 text-gray-400"
            viewBox="0 0 14 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          >
            <line x1="7" y1="2" x2="7" y2="14" />
            <polyline points="4,10 7,14 10,10" />
          </svg>
        ) : isAscending ? (
          <svg
            className="w-3.5 h-4 text-blue-600"
            viewBox="0 0 14 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          >
            <line x1="7" y1="2" x2="7" y2="14" />
            <polyline points="4,6 7,2 10,6" />
          </svg>
        ) : (
          <svg
            className="w-3.5 h-4 text-blue-600"
            viewBox="0 0 14 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          >
            <line x1="7" y1="2" x2="7" y2="14" />
            <polyline points="4,10 7,14 10,10" />
          </svg>
        )}
      </span>
    );
  };

  const downloadCSV = () => {
    const headers = [
      "S No",
      labels.csvIdHeader,
      labels.csvCapacityHeader,
      "Entry Date",
      "Days in Yard",
      "At Entry",
      "Post De-stuffing",
      "After Repair",
      "During Stuffing",
      "Post Stuffing",
      "At Exit",
    ];
    const getStageStatus = (row, stageName) => {
      const s = row?.scan_stages?.find((st) => st?.stage_name === stageName);
      if (!s) return "-";
      if (s?.report_published) return "Approved";
      if (s?.reported_to_manager) return "To Be Approved";
      return "To Be Reviewed";
    };
    const csvRows = sortedScans.map((row, i) => [
      i + 1,
      row.container_number || "",
      row.container_capacity || "",
      row.entry_time
        ? new Date(
            row.entry_time.replace("T", " ").replace("Z", ""),
          ).toLocaleDateString("en-GB")
        : "-",
      row.entry_time
        ? (() => {
            const t = getTimeInYard(row.entry_time);
            const parts = [];
            if (t.days > 0) parts.push(`${t.days}d`);
            if (t.hours > 0) parts.push(`${t.hours}h`);
            if (t.minutes > 0) parts.push(`${t.minutes}m`);
            return `${parts.join(" ") || "0m"} (${row.is_in_yard ? "At Yard" : "Exited"})`;
          })()
        : "-",
      ...[
        "At Entry",
        "Post De-stuffing",
        "After Repair",
        "During Stuffing",
        "Post Stuffing",
        "At Exit",
      ].map((s) => getStageStatus(row, s)),
    ]);
    const csvContent = [
      headers.join(","),
      ...csvRows.map((r) =>
        r
          .map((c) => {
            const s = String(c);
            return s.includes(",") || s.includes('"')
              ? `"${s.replace(/"/g, '""')}"`
              : s;
          })
          .join(","),
      ),
    ].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.setAttribute("href", URL.createObjectURL(blob));
    link.setAttribute(
      "download",
      `csv-report-${new Date().toISOString().split("T")[0]}.csv`,
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const filteredData = getFilteredData(scansData);
  const sortedScans = getSortedData(filteredData);
  const paginatedScans = sortedScans.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const allCount = scansData.length;
  const inYardCount = scansData.filter((i) => i.is_in_yard === true).length;
  const outOfYardCount = scansData.filter((i) => i.is_in_yard === false).length;

  const handleDelete = async (scanId) => {
    if (
      !window.confirm(
        "Are you sure you want to permanently delete this scan? This cannot be undone.",
      )
    )
      return;
    setDeletingId(scanId);
    try {
      await deleteScan(scanId);
      dispatch(removeScan(scanId));
      successMessage("Scan deleted successfully.");
    } catch (err) {
      errorMessage(getErrorMessage(err) || "Failed to delete scan.");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <>
      {isLoading ? (
        <div className="flex flex-1 items-center justify-center">
          <Loader color="primary" size={40} />
        </div>
      ) : (
        <div ref={scrollContainerRef}>
          <HeaderForm
            yardOptions={yardOptions}
            siteLabel="Yard"
            shippingLineOptions={shippingLineOptions}
            allShippingLines={allShippingLines}
            onSubmit={handleSubmit}
            dropDownLabel={labels.dropDownLabel}
            containerIdLabel={labels.idLabel}
            isFilterMode
          />

          <div className="p-6 mt-4 bg-white rounded-2xl shadow-[0_2px_14px_rgba(15,28,53,0.07)] overflow-x-auto">
            <div className="border-b mb-6">
              <div className="flex items-end justify-between">
                <div className="flex items-center gap-6">
                  <button
                    onClick={() => handleTabChange("all")}
                    className={`focus:outline-none pb-3 px-2 font-medium transition-colors ${activeTab === "all" ? "border-b-2 border-blue-600 text-blue-600" : "text-gray-600 hover:text-gray-800"}`}
                  >
                    All{" "}
                    <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-gray-100">
                      {allCount}
                    </span>
                  </button>
                  <button
                    onClick={() => handleTabChange("at_yard")}
                    className={`focus:outline-none pb-3 px-2 font-medium transition-colors ${activeTab === "at_yard" ? "border-b-2 border-blue-600 text-blue-600" : "text-gray-600 hover:text-gray-800"}`}
                  >
                    At Yard{" "}
                    <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-700">
                      {inYardCount}
                    </span>
                  </button>
                  <button
                    onClick={() => handleTabChange("out_of_yard")}
                    className={`focus:outline-none pb-3 px-2 font-medium transition-colors ${activeTab === "out_of_yard" ? "border-b-2 border-blue-600 text-blue-600" : "text-gray-600 hover:text-gray-800"}`}
                  >
                    Exited{" "}
                    <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-700">
                      {outOfYardCount}
                    </span>
                  </button>
                </div>
                <div className="flex items-center gap-4 mb-3">
                  <div
                    style={{
                      position: "relative",
                      display: "inline-flex",
                      alignItems: "center",
                    }}
                  >
                    <svg
                      style={{
                        position: "absolute",
                        left: 11,
                        width: 15,
                        height: 15,
                        color: "#94a3b8",
                        pointerEvents: "none",
                      }}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z"
                      />
                    </svg>
                    <input
                      type="text"
                      placeholder={labels.searchPlaceholder}
                      value={searchQuery}
                      onChange={handleSearchChange}
                      className="w-56 text-sm font-medium text-slate-800 placeholder-slate-400 border border-slate-200 rounded-xl bg-slate-50 focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 focus:bg-white transition-all duration-200"
                      style={{
                        height: 38,
                        paddingLeft: 32,
                        paddingRight: 14,
                      }}
                    />
                  </div>
                  <button
                    onClick={downloadCSV}
                    disabled={sortedScans.length === 0}
                    className="flex items-center gap-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed transition-all duration-200"
                    style={{
                      height: 38,
                      borderRadius: 10,
                      padding: "0 16px",
                    }}
                  >
                    <svg
                      className="w-4 h-4 flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                    Download CSV
                  </button>
                </div>
              </div>
            </div>

            <div className="border-none rounded-lg overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    {listTableHeaders.map((header) => (
                      <th
                        key={header.key}
                        className={`px-4 py-2 text-sm border border-t-0 font-medium text-gray-500 ${header.sortable ? "cursor-pointer hover:bg-gray-100 select-none" : ""}`}
                        onClick={() =>
                          header.sortable && handleSort(header.key)
                        }
                      >
                        <div className="flex items-center justify-center">
                          {header.label}
                          {header.sortable && (
                            <SortIcon columnKey={header.key} />
                          )}
                        </div>
                      </th>
                    ))}
                    <th className="w-64 text-center py-2 border border-t-0 text-sm font-medium text-gray-500">
                      Reports
                      <div className="flex mt-3 pt-3 px-4 border-t g-45">
                        {[
                          "At Entry",
                          "Post De-stuffing",
                          "After Repair",
                          "During Stuffing",
                          "Post Stuffing",
                          "At Exit",
                        ].map((s) => (
                          <span key={s} className="w-15">
                            {s}
                          </span>
                        ))}
                      </div>
                    </th>
                    {roleName === SUPER_ADMIN && (
                      <th className="px-4 py-2 text-sm border border-t-0 font-medium text-gray-500">
                        Actions
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y text-center">
                  {paginatedScans.length > 0 ? (
                    paginatedScans.map((row, index) => (
                      <tr
                        key={row.scan_id}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="border border-t-0 px-4 py-3 text-sm">
                          {(currentPage - 1) * itemsPerPage + index + 1}
                        </td>
                        <td className="border border-t-0 px-4 py-3 text-sm">
                          {row.container_number}
                        </td>
                        <td className="border border-t-0 px-4 py-3 text-sm">
                          {row.container_capacity || "-"}
                        </td>
                        <td className="border border-t-0 px-4 py-3 text-sm">
                          {row.entry_time
                            ? new Date(
                                row.entry_time
                                  .replace("T", " ")
                                  .replace("Z", ""),
                              ).toLocaleDateString("en-GB", {
                                day: "2-digit",
                                month: "2-digit",
                                year: "numeric",
                              })
                            : "-"}
                        </td>
                        <td className="border border-t-0 px-4 py-3 text-sm">
                          {row.entry_time ? (
                            <LiveTimeInYard
                              entryTime={row.entry_time}
                              isInYard={row.is_in_yard}
                            />
                          ) : (
                            <span>-</span>
                          )}
                        </td>
                        <td className="border border-t-0 px-4 py-3">
                          <div className="flex justify-center items-center g-45">
                            {[
                              "At Entry",
                              "Post De-stuffing",
                              "After Repair",
                              "During Stuffing",
                              "Post Stuffing",
                              "At Exit",
                            ].map((stageName) => {
                              const stageObj = row?.scan_stages?.find(
                                (s) => s?.stage_name === stageName,
                              );
                              const color = stageObj?.report_published
                                ? "green"
                                : stageObj?.reported_to_manager
                                  ? "yellow"
                                  : "red";
                              return (
                                <ActionButton
                                  key={`${row.scan_id}_${stageName}`}
                                  id={stageObj?.scan_stage_id || null}
                                  reportKey={`${row.scan_id}_${stageName}`}
                                  color={color}
                                  onClick={saveScrollPosition}
                                />
                              );
                            })}
                          </div>
                        </td>
                        {roleName === SUPER_ADMIN && (
                          <td className="border border-t-0 px-4 py-3">
                            <div className="flex items-center justify-center gap-2">
                              <button
                                title="Delete scan"
                                disabled={deletingId === row.scan_id}
                                onClick={() => handleDelete(row.scan_id)}
                                className="w-8 h-8 flex items-center justify-center rounded-lg border border-red-200 text-red-500 hover:bg-red-50 transition disabled:opacity-40 disabled:cursor-not-allowed"
                              >
                                {deletingId === row.scan_id ? (
                                  <span
                                    style={{
                                      width: 12,
                                      height: 12,
                                      border: "2px solid #ef4444",
                                      borderTopColor: "transparent",
                                      borderRadius: "50%",
                                      display: "inline-block",
                                      animation: "spin 0.7s linear infinite",
                                    }}
                                  />
                                ) : (
                                  <Trash2 size={14} />
                                )}
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={
                          listTableHeaders.length +
                          1 +
                          (roleName === SUPER_ADMIN ? 1 : 0)
                        }
                        className="border px-4 py-8 text-center text-gray-500"
                      >
                        {activeTab === "all"
                          ? "No data available for this filter"
                          : activeTab === "at_yard"
                            ? "No containers at yard"
                            : "No exited containers"}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between mt-1">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
              />
            </div>
          </div>
        </div>
      )}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </>
  );
};

export default YardReport;